import { useState, useEffect } from "react";
import { Button, Modal, Form, Spinner } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import API from "../../axios.jsx";
import { obtenerCatalogosCacheados } from "../api/catalogosCache";
import {
  apiCrearJornada, apiEditarJornada, apiIniciarJornada, apiCerrarJornada, apiReabrirJornada,
  apiEliminarJornada, apiListarLideres, guardarJornadaOffline, generarCodigoJornada, hoyLocal,
  fmtFecha, avisarCambioJornadas,
} from "./jornadasBac";

// ════════════════════════════════════════════════════════════════════
// Pestaña "Jornadas" — cada BAC es una jornada de la comunidad.
//   · Cualquier usuario abre una jornada en campo y queda como líder.
//   · El admin puede programarla con antelación y asignar el líder.
//   · Solo el líder la cierra, adjuntando el informe de la BAC (PDF).
//   · Solo el admin la reabre (con motivo) o la elimina (si está vacía).
// ════════════════════════════════════════════════════════════════════

const VERDE = "#16342a";
const ESTADO = {
  abierta:    { txt: "Abierta",    bg: "#e3f1e8", fg: "#1f6b43" },
  programada: { txt: "Programada", bg: "#e8eefb", fg: "#2c4f9e" },
  cerrada:    { txt: "Cerrada",    bg: "#eef0f2", fg: "#4a5560" },
};

const urlArchivo = (u) => (u ? `${String(API.defaults.baseURL || "").replace(/\/$/, "")}${u}` : null);
const msg = (e) => e?.response?.data?.message || e?.message || "Error desconocido";

function Contador({ n, label, tono = "", titulo }) {
  return (
    <div className={`bac-cifra ${tono ? `is-${tono}` : ""}`} title={titulo}>
      <dd>{n ?? "—"}</dd>
      <dt>{label}</dt>
    </div>
  );
}

// ── Formulario abrir / programar / editar ────────────────────────────
function FormJornadaModal({ show, modo: modoBase, jornada, ctx, esAdmin, online, onClose, onGuardada }) {
  // Al crear, el admin puede asignar la jornada a otro líder (queda
  // programada hasta que ese líder la inicie).
  const [programarOtro, setProgramarOtro] = useState(false);
  const modo = modoBase === "abrir" && programarOtro ? "programar" : modoBase;
  const [entrevistadores, setEntrevistadores] = useState([]);
  const [opcionesEntrev, setOpcionesEntrev] = useState([]);
  const [fechaInicio, setFechaInicio] = useState(hoyLocal());
  const [objetivo, setObjetivo] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [lideres, setLideres] = useState([]);
  const [liderId, setLiderId] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => { if (show) setProgramarOtro(false); }, [show]);

  useEffect(() => {
    if (!show) return;
    obtenerCatalogosCacheados().then((c) => setOpcionesEntrev(c["entrevistadores"] || [])).catch(() => {});
    setEntrevistadores(jornada?.entrevistadores || []);
    setFechaInicio(jornada?.fecha_inicio ? String(jornada.fecha_inicio).slice(0, 10) : hoyLocal());
    setObjetivo(jornada?.objetivo || "");
    setObservaciones(jornada?.observaciones || "");
    setLiderId(jornada?.lider_id ? String(jornada.lider_id) : "");
    if (esAdmin && online && (modo === "programar" || modo === "editar")) {
      apiListarLideres().then((r) => setLideres(r.data || [])).catch(() => setLideres([]));
    }
  }, [show, modo, jornada, esAdmin, online]);

  const titulo = modo === "editar" ? "Editar jornada" : "Crear jornada nueva";

  const guardar = async () => {
    if (modo === "programar" && !liderId) return Swal.fire("Falta el líder", "Elige quién será el líder de la BAC.", "warning");
    setGuardando(true);
    let codigoFinal = null;
    try {
      if (modo === "editar") {
        const datos = { entrevistadores, objetivo, observaciones, fecha_inicio: fechaInicio };
        if (esAdmin && liderId && String(liderId) !== String(jornada.lider_id)) datos.lider_id = Number(liderId);
        await apiEditarJornada(jornada.codigo, datos);
        Swal.fire({ icon: "success", title: "Jornada actualizada", timer: 1500, showConfirmButton: false });
      } else {
        const datos = {
          codigo: generarCodigoJornada(ctx.territorio, ctx.comunidad),
          territorio: ctx.territorio, comunidad: ctx.comunidad,
          fecha_inicio: fechaInicio, entrevistadores, objetivo,
        };
        codigoFinal = datos.codigo;
        if (modo === "programar") {
          await apiCrearJornada({ ...datos, programar: true, lider_id: Number(liderId) });
          codigoFinal = null; // programada: no se entra todavía
          Swal.fire({ icon: "success", title: "Jornada programada", timer: 1600, showConfirmButton: false });
        } else if (!online) {
          await guardarJornadaOffline(datos);
          Swal.fire({ icon: "info", title: "Jornada abierta sin señal", text: "Ya puedes registrar visitas. Se sincroniza al recuperar internet.", confirmButtonColor: VERDE });
        } else {
          try {
            await apiCrearJornada(datos);
            Swal.fire({ icon: "success", title: "Jornada abierta", text: "Tú quedas como líder de la BAC.", timer: 1800, showConfirmButton: false });
          } catch (e) {
            if (e?.response?.status === 409 && e.response.data?.jornada) {
              const j = e.response.data.jornada;
              codigoFinal = j.codigo;
              await Swal.fire({ icon: "info", title: "Ya tienes una jornada abierta", html: `En <b>${ctx.comunidad}</b> ya eres líder de una jornada abierta (desde ${fmtFecha(j.fecha_inicio)}).<br>Vas a ingresar a esa jornada.`, confirmButtonColor: VERDE });
            } else if (!e?.response) {
              await guardarJornadaOffline(datos);
              Swal.fire({ icon: "info", title: "Jornada abierta sin señal", text: "Se sincroniza al recuperar internet.", confirmButtonColor: VERDE });
            } else throw e;
          }
        }
      }
      avisarCambioJornadas();
      onGuardada?.(codigoFinal);
      onClose();
    } catch (e) {
      Swal.fire("No se pudo guardar", msg(e), "error");
    } finally {
      setGuardando(false);
    }
  };

  const opts = [...new Set([...opcionesEntrev, ...entrevistadores])].map((v) => ({ value: v, label: v }));

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton><Modal.Title style={{ fontSize: 18 }}>{titulo}</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className="mb-3 p-2 rounded" style={{ background: "#f5f6f0", fontSize: 13 }}>
          <b>{ctx.territorio}</b> · {ctx.comunidad}
          {modo === "abrir" && <div className="text-muted" style={{ fontSize: 12 }}>Tú quedas como líder de esta jornada y solo tú podrás cerrarla. Cada equipo trabaja en su propia jornada.</div>}
          {modo === "abrir" && !online && <div style={{ fontSize: 12, color: "#9a6b00" }}>Sin conexión: la jornada se guarda en el dispositivo y sube después.</div>}
        </div>

        {modoBase === "abrir" && esAdmin && online && (
          <Form.Check type="switch" id="programar-otro" className="mb-3"
            label="Asignarla a otro líder (queda programada hasta que la inicie)"
            checked={programarOtro} onChange={(e) => setProgramarOtro(e.target.checked)} />
        )}

        {(modo === "programar" || (modo === "editar" && esAdmin && online)) && (
          <Form.Group className="mb-3">
            <Form.Label>Líder de la BAC <span className="text-danger">*</span></Form.Label>
            <Form.Select value={liderId} onChange={(e) => setLiderId(e.target.value)}>
              <option value="">Elegir…</option>
              {lideres.map((l) => <option key={l.id} value={l.id}>{l.nombre}{l.rol === "admin" ? " (admin)" : ""}</option>)}
            </Form.Select>
            <Form.Text muted>El líder es quien inicia y cierra la jornada.</Form.Text>
          </Form.Group>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Fecha de inicio</Form.Label>
          <Form.Control type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} />
          <Form.Text muted>La BAC puede durar varios días; la fecha de cierre se pone al cerrarla.</Form.Text>
        </Form.Group>

        {/* Al crear la jornada no se piden: salen solos de quienes registran
            visitas. Al editar se pueden ajustar si hace falta. */}
        {modo === "editar" && (
        <Form.Group className="mb-3">
          <Form.Label>Entrevistadores</Form.Label>
          <Select isMulti placeholder="Buscar y agregar…" options={opts} closeMenuOnSelect={false}
            value={entrevistadores.map((v) => ({ value: v, label: v }))}
            onChange={(sel) => setEntrevistadores((sel || []).map((s) => s.value))}
            noOptionsMessage={() => "Sin coincidencias en la lista de entrevistadores"} />
        </Form.Group>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Objetivo / observación inicial</Form.Label>
          <Form.Control as="textarea" rows={2} value={objetivo} onChange={(e) => setObjetivo(e.target.value)} maxLength={2000} />
        </Form.Group>

        {modo === "editar" && (
          <Form.Group>
            <Form.Label>Observaciones</Form.Label>
            <Form.Control as="textarea" rows={2} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} maxLength={4000} />
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={guardando}>Cancelar</Button>
        <Button style={{ background: VERDE, border: "none" }} onClick={guardar} disabled={guardando}>
          {guardando ? <Spinner size="sm" /> : modo === "editar" ? "Guardar cambios" : modo === "programar" ? "Crear y asignar" : "Crear jornada"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ── Cerrar (solo el líder, con informe PDF) ──────────────────────────
function CerrarJornadaModal({ show, jornada, onClose }) {
  const [fechaFin, setFechaFin] = useState(hoyLocal());
  const [observaciones, setObservaciones] = useState("");
  const [archivo, setArchivo] = useState(null);
  const [progreso, setProgreso] = useState(null);

  useEffect(() => {
    if (!show) return;
    setFechaFin(hoyLocal()); setObservaciones(jornada?.observaciones || ""); setArchivo(null); setProgreso(null);
  }, [show, jornada]);

  const cerrar = async () => {
    if (!archivo) return Swal.fire("Falta el informe", "Adjunta el informe de la BAC en PDF para cerrar la jornada.", "warning");
    if (!/\.pdf$/i.test(archivo.name) && archivo.type !== "application/pdf") return Swal.fire("Formato no válido", "El informe debe ser un PDF.", "warning");
    if (archivo.size > 20 * 1024 * 1024) return Swal.fire("Archivo muy pesado", "El informe no puede pasar de 20 MB.", "warning");
    if (fechaFin < String(jornada.fecha_inicio).slice(0, 10)) return Swal.fire("Fecha no válida", "La fecha de cierre no puede ser anterior al inicio.", "warning");
    const ok = await Swal.fire({
      icon: "question", title: "¿Cerrar la jornada?",
      html: "Después de cerrada nadie podrá registrar visitas en ella.<br>Solo el administrador puede reabrirla.",
      showCancelButton: true, confirmButtonText: "Sí, cerrar", cancelButtonText: "Cancelar", confirmButtonColor: VERDE,
    });
    if (!ok.isConfirmed) return;
    const fd = new FormData();
    fd.append("fecha_fin", fechaFin);
    fd.append("observaciones", observaciones);
    fd.append("informe", archivo);
    setProgreso(0);
    try {
      await apiCerrarJornada(jornada.codigo, fd, { onUploadProgress: (e) => e.total && setProgreso(Math.round((e.loaded * 100) / e.total)) });
      Swal.fire({ icon: "success", title: "Jornada cerrada", timer: 1600, showConfirmButton: false });
      avisarCambioJornadas();
      onClose();
    } catch (e) {
      Swal.fire("No se pudo cerrar", msg(e), "error");
    } finally {
      setProgreso(null);
    }
  };

  if (!jornada) return null;
  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton><Modal.Title style={{ fontSize: 18 }}>🔒 Cerrar jornada BAC</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className="mb-3 p-2 rounded" style={{ background: "#f5f6f0", fontSize: 13 }}>
          <b>{jornada.comunidad}</b> · inicio {fmtFecha(jornada.fecha_inicio)} · líder {jornada.lider_nombre}
        </div>
        <Form.Group className="mb-3">
          <Form.Label>Informe de la BAC (PDF) <span className="text-danger">*</span></Form.Label>
          <Form.Control type="file" accept="application/pdf,.pdf" onChange={(e) => setArchivo(e.target.files?.[0] || null)} />
          <Form.Text muted>Es el soporte obligatorio para cerrar. Máximo 20 MB.</Form.Text>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Fecha de cierre</Form.Label>
          <Form.Control type="date" value={fechaFin} min={String(jornada.fecha_inicio).slice(0, 10)} onChange={(e) => setFechaFin(e.target.value)} />
        </Form.Group>
        <Form.Group>
          <Form.Label>Observaciones de cierre</Form.Label>
          <Form.Control as="textarea" rows={2} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} maxLength={4000} />
        </Form.Group>
        {progreso !== null && (
          <div className="mt-3" style={{ height: 8, background: "#e9ecef", borderRadius: 8 }}>
            <div style={{ width: `${progreso}%`, height: 8, background: VERDE, borderRadius: 8, transition: "width .2s" }} />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={progreso !== null}>Cancelar</Button>
        <Button variant="danger" onClick={cerrar} disabled={progreso !== null}>
          {progreso !== null ? `Subiendo ${progreso}%` : "Cerrar jornada"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ── Tarjeta de una jornada ───────────────────────────────────────────
function TarjetaJornada({ j, usuario, esAdmin, online, onEditar, onCerrar, onVer }) {
  const esLider = j.lider_id != null && Number(j.lider_id) === Number(usuario?.id);
  // Solo quien abrió (en campo) o programó la jornada puede eliminarla
  const esCreador = j.origen === "campo" ? esLider : j.origen === "programada" ? (!!j.creado_por && j.creado_por === usuario?.nombre) : false;
  const est = ESTADO[j.estado] || ESTADO.cerrada;
  const r = j.resumen;
  const sinRed = () => Swal.fire("Sin conexión", "Esta acción necesita internet.", "warning");

  const iniciar = async () => {
    if (!online) return sinRed();
    try { await apiIniciarJornada(j.codigo); avisarCambioJornadas(); Swal.fire({ icon: "success", title: "Jornada iniciada", timer: 1400, showConfirmButton: false }); }
    catch (e) { Swal.fire("No se pudo iniciar", msg(e), "error"); }
  };
  const reabrir = async () => {
    if (!online) return sinRed();
    const { value: motivo } = await Swal.fire({
      title: "Reabrir jornada", input: "textarea", inputLabel: "Motivo de la reapertura",
      inputPlaceholder: "Ej.: faltó registrar viviendas del sector norte", showCancelButton: true,
      confirmButtonText: "Reabrir", cancelButtonText: "Cancelar", confirmButtonColor: VERDE,
      inputValidator: (v) => (!v || v.trim().length < 10 ? "Escribe el motivo (mínimo 10 caracteres)" : undefined),
    });
    if (!motivo) return;
    try { await apiReabrirJornada(j.codigo, motivo.trim()); avisarCambioJornadas(); Swal.fire({ icon: "success", title: "Jornada reabierta", timer: 1400, showConfirmButton: false }); }
    catch (e) { Swal.fire("No se pudo reabrir", msg(e), "error"); }
  };
  const eliminar = async () => {
    if (!online) return sinRed();
    const ok = await Swal.fire({ icon: "warning", title: "¿Eliminar la jornada?", text: "Solo se puede eliminar si no tiene visitas.", showCancelButton: true, confirmButtonText: "Eliminar", cancelButtonText: "Cancelar", confirmButtonColor: "#b3261e" });
    if (!ok.isConfirmed) return;
    try { await apiEliminarJornada(j.codigo); avisarCambioJornadas(); Swal.fire({ icon: "success", title: "Jornada eliminada", timer: 1400, showConfirmButton: false }); }
    catch (e) { Swal.fire("No se pudo eliminar", msg(e), "error"); }
  };

  const rango = j.fecha_fin && j.fecha_fin !== j.fecha_inicio
    ? `${fmtFecha(j.fecha_inicio)} – ${fmtFecha(j.fecha_fin)}`
    : j.fecha_fin ? fmtFecha(j.fecha_inicio) : `Desde ${fmtFecha(j.fecha_inicio)}`;

  return (
    <article className={`bac-jornada is-${j.estado}`}>
      <div className="bac-jornada-top">
        <div className="bac-jornada-info">
          <div className="bac-jornada-titulo">
            <h3>{rango}</h3>
            <span className={`bac-estado is-${j.estado}`}>{est.txt}</span>
            {j.origen === "historica" && <span className="bac-etiqueta" title="Registros anteriores al módulo de jornadas">Histórica</span>}
            {j._pendiente && !j._error && <span className="bac-etiqueta is-aviso">Sin sincronizar</span>}
            {j._error && <span className="bac-etiqueta is-error" title={j._error}>Error al subir</span>}
          </div>
          <p className="bac-jornada-lider">
            Líder <strong>{j.lider_nombre || "—"}</strong>{esLider && <span className="bac-tu">tú</span>}
          </p>
          {j.objetivo && <p className="bac-jornada-nota">{j.objetivo}</p>}
          {j.motivo_reapertura && <p className="bac-jornada-nota is-aviso">Reabierta por {j.reabierta_por}: {j.motivo_reapertura}</p>}
        </div>
        <div className="bac-jornada-acciones">
          {j.informe_url && <Button size="sm" variant="outline-secondary" href={urlArchivo(j.informe_url)} target="_blank" rel="noreferrer">Ver informe</Button>}
          {j.estado === "programada" && (esLider || esAdmin) && <Button size="sm" variant="success" onClick={iniciar}>Iniciar</Button>}
          {!j._pendiente && j.estado !== "cerrada" && (esLider || esAdmin) && <Button size="sm" variant="outline-secondary" onClick={() => (online ? onEditar(j) : sinRed())}>Editar</Button>}
          {!j._pendiente && j.estado === "abierta" && esLider && <Button size="sm" variant="outline-danger" onClick={() => (online ? onCerrar(j) : sinRed())}>Cerrar jornada</Button>}
          {j.estado === "cerrada" && esAdmin && <Button size="sm" variant="outline-secondary" onClick={reabrir}>Reabrir</Button>}
          {!j._pendiente && esAdmin && esCreador && !(r?.intentos > 0) && <Button size="sm" variant="outline-danger" onClick={eliminar}>Eliminar</Button>}
          {onVer && <Button size="sm" className="bac-btn-ingresar" onClick={() => onVer(j)}>Ingresar</Button>}
        </div>
      </div>

      {r && (
        <dl className="bac-cifras">
          <Contador n={r.viviendas} label="Viviendas" titulo={`${r.intentos} intentos de visita`} />
          <Contador n={r.efectivas} label="Efectivas" tono="hoja" />
          <Contador n={r.no_efectivas} label="No efectivas" tono="ambar" />
          <Contador n={r.personas_encontradas} label="Personas PAI" />
          <Contador n={r.personas_sin_carnet} label="Sin carnet" tono="rojo" />
          <Contador n={r.vacunadas_barrido} label="Vacunadas" tono="hoja" />
          <Contador n={r.no_vacunadas_barrido} label="No vacunadas" tono="ambar" />
          <Contador n={r.mrcv} label="MRCV" tono="agua" />
        </dl>
      )}
    </article>
  );
}

// ── Pestaña ──────────────────────────────────────────────────────────
export default function TablaJornadasBac({ ctx, usuario, esAdmin, online, jornadasBac, onVerVisitas }) {
  const { jornadas, cargando, desdeCache, recargar } = jornadasBac;
  const [form, setForm] = useState({ show: false, modo: "abrir", jornada: null });
  const [cerrar, setCerrar] = useState(null);
  const [filtro, setFiltro] = useState("todas");

  useEffect(() => {
    const f = (ev) => {
      const d = ev.detail || {};
      Swal.fire({ icon: "info", title: "Jornada unificada", html: `Ya tenías una jornada abierta en <b>${d.comunidad}</b>.<br>${d.visitas ? `Tus ${d.visitas} visita(s) quedaron dentro de esa jornada.` : "Las visitas que registres irán dentro de esa jornada."}`, confirmButtonColor: VERDE });
    };
    window.addEventListener("bac-jornada-fusionada", f);
    return () => window.removeEventListener("bac-jornada-fusionada", f);
  }, []);

  if (!ctx?.territorio) return <div className="text-muted">Elige territorio y comunidad.</div>;

  const visibles = jornadas.filter((j) => filtro === "todas" || j.estado === filtro);

  return (
    <div className="bac-elegir">
      <header className="bac-elegir-cab">
        <h2>Elige la jornada</h2>
        <p>Al ingresar, las visitas, el barrido PAI, el MRCV, el mapa y el resumen muestran solo lo de esa jornada.</p>
      </header>

      {/* Solo los administradores crean jornadas */}
      {esAdmin ? (
        <div className="bac-crear-jornada">
          <Button className="bac-btn-accion" onClick={() => setForm({ show: true, modo: "abrir", jornada: null })}>Crear jornada nueva</Button>
        </div>
      ) : !jornadas.some((j) => j.estado === "abierta") && (
        <p className="bac-nota-gestor">No hay jornadas abiertas en {ctx.comunidad}. Un administrador debe crear la jornada para poder registrar visitas.</p>
      )}

      <div className="bac-filtro-jornadas">
        <div className="bac-segmentos" role="tablist">
          {[["todas", "Todas"], ["abierta", "Abiertas"], ["programada", "Programadas"], ["cerrada", "Cerradas"]].map(([k, t]) => (
            <button key={k} type="button" role="tab" aria-selected={filtro === k} className={filtro === k ? "is-on" : ""} onClick={() => setFiltro(k)}>
              {t} <span>{k === "todas" ? jornadas.length : jornadas.filter((j) => j.estado === k).length}</span>
            </button>
          ))}
        </div>
        <div className="d-flex align-items-center gap-2">
          {desdeCache && <span className="bac-etiqueta is-aviso">Sin conexión: datos guardados</span>}
          <Button size="sm" variant="outline-secondary" onClick={recargar} disabled={cargando}>{cargando ? <Spinner size="sm" /> : "Actualizar"}</Button>
        </div>
      </div>

      {visibles.length === 0 && !cargando && <p className="bac-vacio">No hay jornadas {filtro !== "todas" ? `${{ abierta: "abiertas", programada: "programadas", cerrada: "cerradas" }[filtro]}` : "registradas"} en {ctx.comunidad}.</p>}
      {visibles.map((j) => (
        <TarjetaJornada key={j.codigo} j={j} usuario={usuario} esAdmin={esAdmin} online={online}
          onEditar={(jj) => setForm({ show: true, modo: "editar", jornada: jj })}
          onCerrar={setCerrar} onVer={onVerVisitas} />
      ))}

      <FormJornadaModal show={form.show} modo={form.modo} jornada={form.jornada} ctx={ctx} esAdmin={esAdmin} online={online}
        onClose={() => setForm((f) => ({ ...f, show: false }))}
        onGuardada={(codigo) => { recargar(); if (codigo && onVerVisitas) onVerVisitas({ codigo }); }} />
      <CerrarJornadaModal show={!!cerrar} jornada={cerrar} onClose={() => setCerrar(null)} />
    </div>
  );
}

// ── Cabecera de una jornada abierta en pantalla (pestaña "Resumen") ──
// Misma tarjeta con sus contadores y acciones (editar, cerrar con
// informe, reabrir), para gestionar la jornada sin salir de ella.
export function ResumenJornada({ jornada, ctx, usuario, esAdmin, online }) {
  const [form, setForm] = useState({ show: false, jornada: null });
  const [cerrar, setCerrar] = useState(null);
  if (!jornada) return null;
  return (
    <div className="bac-resumen-jornada">
      <TarjetaJornada j={jornada} usuario={usuario} esAdmin={esAdmin} online={online}
        onEditar={(jj) => setForm({ show: true, jornada: jj })} onCerrar={setCerrar} />
      <FormJornadaModal show={form.show} modo="editar" jornada={form.jornada} ctx={ctx} esAdmin={esAdmin} online={online}
        onClose={() => setForm((f) => ({ ...f, show: false }))} />
      <CerrarJornadaModal show={!!cerrar} jornada={cerrar} onClose={() => setCerrar(null)} />
    </div>
  );
}
